import moment, { Moment } from 'moment';
import { flattenDeep } from 'lodash';
import { getDaysBetweenDatesMatchingWeekday } from '@bringg-frontend/utils';
import { type CreateSpeedFactor } from '@bringg/dashboard-sdk/dist/SpeedFactor/Service/SpeedFactor.service';
import { EndOfWeekDay } from '@bringg/types';

import { getDayAndMonthNumbers } from './utils/date';
import { getIntervals, formatFactorToPercents } from './utils/generic';
import { prepareRanges } from './utils/split-by-utc-midnight';
import { RangeRecord } from './utils/types';
import { isFlatSpeedFactor } from './speed-factor-grouping';
import { SpeedFactorGroup, SpeedFactorGroupType } from './speed-factor-grouping/types';
import { getBaseWeekDayShiftInUTC, getCorrectedISOWeekDays, daysFromEndOfWeekDayToIsoWeekday } from './utils/week-day';
import { dstManager } from './utils/dst-manager';
import { trafficVariablesConfig } from './traffic-variables-config';

// Service Area Speed Factors CRUD Schema Examples and notes: https://bringg.atlassian.net/wiki/spaces/~727657759/pages/3400237068/Service+Area+Speed+Data+CRUD+Schema+Examples
const CURRENT_YEAR = new Date().getFullYear();
const DATE_FORMAT = 'YYYY-MM-DD';

export type ActionType = 'current' | 'current_and_following' | 'all';

export type CalendarEvent = {
	id: string;
	start: string | Date;
	end: string | Date;
	title: string;
	originalEnd: string | Date; // delete at the end
	originalStart: string | Date; // delete at the end
	originalEvent: SpeedFactorGroup;
};

export type EventFormData = {
	factor: number;
	timeFrame: 'specificHours' | 'allDay';
	service_area_id?: number;
	applicableDays?: EndOfWeekDay[];
	effectiveDates?: [Moment, Moment];
	intervals?: [Moment, Moment];
};

export const getTimeSlotUtcEffectiveDates = ({
	start,
	end,
	timezone = 'UTC'
}: {
	start: Moment;
	end: Moment;
	timezone?: string;
}) => {
	const timezoneOffset = moment.tz(timezone).utcOffset() / 60;
	const [utcStart, utcEnd] = [start.clone().startOf('day').utc(), end.clone().endOf('day').utc()];

	if (timezoneOffset === 0) {
		return {
			start: utcStart,
			end: utcEnd
		};
	}

	if (timezoneOffset < 0) {
		const timezoneEnd = end.clone().tz(timezone);
		const isEndSameYear = timezoneEnd.year() === utcEnd.year();
		// timezoneOffset < 0 may result in forward day bubbling.
		return {
			// Never bubbles from the startOfTheDay with -NN:00 timezone
			start: utcStart,
			// Prevent bubbling for "end" only if it bubbles to another year
			end: isEndSameYear ? utcEnd : utcEnd.subtract(1, 'day').endOf('day')
		};
	}

	// Make sure timezone is applied
	const timezoneStart = start.clone().tz(timezone);
	const isStartSameYear = timezoneStart.year() === utcStart.year();
	// timezoneOffset > 0 may result in backward day bubbling.
	// The effective end date should never bubble as it is the "end of the day".
	return {
		// Prevent bubbling for "start" only if it bubbles to another year
		start: isStartSameYear ? utcStart : utcStart.add(1, 'day').startOf('day'),
		// Never bubbles from the endOfTheDay with +NN:00 timezone
		end: utcEnd
	};
};

export const buildCreateSpeedFactorPayload = (formData: EventFormData, timezone: string): CreateSpeedFactor[] => {
	const [_, start_month, __, end_month] = getDayAndMonthNumbers(formData.effectiveDates);

	const ranges: RangeRecord[] = [];
	const [effectiveStartDate, effectiveEndDate] = formData.effectiveDates;

	const daysOfWeek = getCorrectedISOWeekDays(
		daysFromEndOfWeekDayToIsoWeekday(formData.applicableDays),
		getBaseWeekDayShiftInUTC({
			type: 'create',
			start: formData.intervals[0],
			timezone
		})
	);

	// In case when start and end of the range in different years
	// split range into two payloads
	if (start_month > end_month) {
		ranges.push({
			effectiveStartDate,
			effectiveEndDate: effectiveStartDate.clone().endOf('year'),
			daysOfWeek,
			formData
		});
		ranges.push({
			effectiveStartDate: effectiveEndDate.clone().startOf('year'),
			effectiveEndDate,
			daysOfWeek,
			formData
		});
	} else {
		ranges.push({ effectiveStartDate, effectiveEndDate, daysOfWeek, formData });
	}

	return prepareRanges({
		ranges,
		timezone,
		operationType: 'create',
		speedFactorData: { service_area_id: formData.service_area_id }
	});
};

export const buildSpeedFactorsFromPayloads = payloadsArray => {
	let speedFactors = [];

	payloadsArray.forEach(payload => {
		const newSpeedFactors = payload.days_of_week.map(day_of_week => {
			return {
				id: null,
				service_area_id: payload.service_area_id,
				merchant_id: 0,
				merchant_uuid: '',
				start_date: `1972-${payload.start_date_month}-${payload.start_date_day}`,
				end_date: `1972-${payload.end_date_month}-${payload.end_date_day}`,
				factor: payload.factor,
				day_of_week: day_of_week,
				interval_start: payload.intervals[0][0],
				interval_end: payload.intervals[0][1],
				original_uuid: '',
				created_at: '',
				updated_at: ''
			};
		});

		speedFactors = [...speedFactors, ...newSpeedFactors];
	});

	return speedFactors;
};

const createEventsForYearShared = ({
	event,
	year,
	timezone,
	transformEndDate
}: {
	event: SpeedFactorGroup;
	year: number;
	timezone: string;
	transformEndDate?: (date: Moment) => Moment;
}) => {
	const [startDate, endDate] = getDatesForCurrentYearFrom1972(event.start_date, event.end_date, year);
	// We don't need those events for the whole year / years
	// Create events dynamically when switching between weeks on the calendar
	const matchingWeekDaysBetween = getDaysBetweenDatesMatchingWeekday({
		startDate: startDate.toDate(),
		endDate: endDate.toDate(),
		timezone: 'UTC',
		// Moment [0,6], server stored [1,7]
		weekDay: event.day_of_week - 1
	});

	return Object.keys(matchingWeekDaysBetween).map(day => {
		let startDateTime = moment.utc(`${moment(day).format(DATE_FORMAT)}T${event.interval_start}`, 'YYYY-MM-DD H');
		let endDateTime = moment.utc(
			`${moment(day).format(DATE_FORMAT)}T${event.interval_end - 1}:59`,
			'YYYY-MM-DD H:mm'
		);

		if (transformEndDate) {
			endDateTime = transformEndDate(endDateTime);
		}

		if (trafficVariablesConfig.enableHandleDST) {
			startDateTime = dstManager.adjustDateForView(startDateTime, timezone);
			endDateTime = dstManager.adjustDateForView(endDateTime, timezone);
		}

		const factor = formatFactorToPercents(event.factor);

		return {
			id: event.id,
			title: `${factor > 0 ? '+' : ''}${factor}%`,
			start: startDateTime.toDate(),
			end: endDateTime.toDate(),
			factor,
			originalStart: startDate.toDate(),
			originalEnd: endDate.toDate(),
			originalEvent: event
		};
	});
};

const createEventsForYear = (event: SpeedFactorGroup, year: number, timezone: string) => {
	if (isFlatSpeedFactor(event)) {
		return createEventsForYearShared({ event, year, timezone });
	} else if (event.groupType === SpeedFactorGroupType.UtcMidnightSplit) {
		return createEventsForYearShared({
			event,
			year,
			timezone,
			transformEndDate: (date: Moment) => date.add(1, 'day')
		});
	}
};

export const normalizeEvents = (
	groupedSpeedFactors: SpeedFactorGroup[],
	yearsCount = 1,
	fromYear = null,
	withPreviousYear = false,
	timezone = 'UTC'
) => {
	const normalizedEvents: CalendarEvent[] = [];
	const years = [];
	const startYear = Number(fromYear) || CURRENT_YEAR;

	// Generate data for previous year, current year + yearsCount more
	const start = withPreviousYear ? -1 : 0;
	for (let i = start; i < yearsCount; i++) {
		years.push(startYear + i);
	}

	years.forEach(year => {
		const yearEvents = groupedSpeedFactors.map(event => createEventsForYear(event, year, timezone));
		normalizedEvents.push(...flattenDeep(yearEvents));
	});

	return normalizedEvents;
};

/**
 * @returns [startDate, endDate] are always returned in UTC
 */
const getDatesForCurrentYearFrom1972 = (startDate: string, endDate: string, toYear = CURRENT_YEAR) => {
	const currentYear = toYear.toString();

	const currentStartDate = moment.utc(startDate.replace('1972', currentYear), DATE_FORMAT);
	const currentEndDate = moment.utc(endDate.replace('1972', currentYear), DATE_FORMAT);

	if (currentStartDate > currentEndDate) {
		// eslint-disable-next-line no-console
		console.warn('Start date is later then end date!', currentStartDate, currentEndDate);
	}

	return [currentStartDate, currentEndDate];
};

export const buildCreateSpeedFactorsOnDelete = (
	calEvent: CalendarEvent,
	groupedSpeedFactors: SpeedFactorGroup[],
	action: ActionType,
	timezone: string
): CreateSpeedFactor[] => {
	if (action === 'all') return [];

	const ranges: RangeRecord[] = [];

	const speedFactor: SpeedFactorGroup = calEvent.originalEvent;
	const calEventYear = moment.utc(calEvent.start).year();

	const [dbStartDate, dbEndDate] = getDatesForCurrentYearFrom1972(
		speedFactor.start_date,
		speedFactor.end_date,
		calEventYear
	);

	const allDaysOfWeek = Array.from(
		new Set(
			groupedSpeedFactors
				.filter(event => event.original_uuid === speedFactor.original_uuid)
				.map(event => event.day_of_week)
		)
	);

	const newEndDate = moment.utc(calEvent.start).subtract(1, 'day');
	if (newEndDate >= dbStartDate) {
		ranges.push({
			effectiveStartDate: dbStartDate,
			effectiveEndDate: newEndDate,
			daysOfWeek: allDaysOfWeek
		});
	}

	// Do nothing for current_and_following
	if (action === 'current') {
		const newStartDate = moment.utc(calEvent.start).startOf('day').add(1, 'day');
		if (dbEndDate >= newStartDate) {
			ranges.push({
				effectiveStartDate: newStartDate,
				effectiveEndDate: dbEndDate,
				daysOfWeek: allDaysOfWeek
			});
		}
	}

	return prepareRanges({
		ranges,
		timezone,
		operationType: 'split-on-delete',
		speedFactorData: speedFactor
	});
};

export const buildCreateSpeedFactorsOnUpdate = (
	calEvent: CalendarEvent,
	groupedSpeedFactors: SpeedFactorGroup[],
	action: ActionType,
	formData: EventFormData,
	timezone: string
): CreateSpeedFactor[] => {
	const ranges: RangeRecord[] = [];

	const speedFactor: SpeedFactorGroup = calEvent.originalEvent;
	const calEventYear = moment.utc(calEvent.start).year();
	const [dbStartDate, dbEndDate] = getDatesForCurrentYearFrom1972(
		speedFactor.start_date,
		speedFactor.end_date,
		calEventYear
	);

	// Get all days of week for newly created speedFactors
	// Filter events by original_uuid and then get unique days of week
	const allDaysOfWeek = Array.from(
		new Set(
			groupedSpeedFactors
				.filter(event => event.original_uuid === speedFactor.original_uuid)
				.map(event => event.day_of_week)
		)
	);

	const daysShift = getBaseWeekDayShiftInUTC({
		type: 'edit',
		prevStart: moment(calEvent.start),
		nextStart: formData.intervals[0]
	});

	// 'all', 'current_and_following' branches don't yet update startDate / endDate
	if (action === 'all') {
		ranges.push({
			effectiveStartDate: dbStartDate,
			effectiveEndDate: dbEndDate,
			daysOfWeek: getCorrectedISOWeekDays(allDaysOfWeek, daysShift),
			formData
		});
	} else if (action === 'current') {
		// 'current' under specific circumstances can be logically equal to 'all'
		// in this case rest - before, after traffic vars can be empty / invisible
		// Create a new range Before
		const newEndDate = moment.utc(calEvent.start).subtract(1, 'day');
		if (newEndDate >= dbStartDate) {
			ranges.push({
				effectiveStartDate: dbStartDate,
				effectiveEndDate: newEndDate,
				daysOfWeek: allDaysOfWeek
			});
		}

		// Create a new range After
		const newStartDate = moment.utc(calEvent.start).startOf('day').add(1, 'day');
		if (dbEndDate >= newStartDate) {
			ranges.push({
				effectiveStartDate: newStartDate,
				effectiveEndDate: dbEndDate,
				daysOfWeek: allDaysOfWeek
			});
		}

		// Create new range Current
		const currentStartDate = moment.utc(calEvent.start).add(daysShift, 'day');
		const currentEndDate = moment.utc(calEvent.start).add(daysShift, 'day');
		// Pass formData for current event
		// Pass only current day as days_of_weeks for current event
		ranges.push({
			effectiveStartDate: currentStartDate,
			effectiveEndDate: currentEndDate,
			daysOfWeek: getCorrectedISOWeekDays([calEvent.originalEvent.day_of_week], daysShift),
			formData,
			adjustEffectiveDates: true
		});
	} else if (action === 'current_and_following') {
		// Create a new range Before
		const newEndDate = moment.utc(calEvent.start).subtract(1, 'day');
		if (newEndDate >= dbStartDate) {
			ranges.push({
				effectiveStartDate: dbStartDate,
				effectiveEndDate: newEndDate,
				daysOfWeek: allDaysOfWeek
			});
		}

		// Create a new range Current + Before
		const newStartDate = moment.utc(calEvent.start).startOf('day');
		if (dbEndDate >= newStartDate) {
			// may also result in empty / invisible events
			ranges.push({
				effectiveStartDate: newStartDate,
				effectiveEndDate: dbEndDate,
				daysOfWeek: getCorrectedISOWeekDays(allDaysOfWeek, daysShift),
				formData
			});
		}
	}

	return prepareRanges({
		ranges,
		timezone,
		operationType: 'update',
		speedFactorData: speedFactor
	});
};

// There are no proper validations on the backend. It means that speed factor with invalid days or intervals could be saved and then you'll have very fun time with debugging on FE
// So think about them as kind of backend validations
// Ideally, the user should not see these errors.
// If the users see it, then somewhere we have an error in the code that generates the data to be sent
export const validateFormData = (formData: EventFormData) => {
	const { allowUtcMidnightCrossingRanges } = trafficVariablesConfig;
	const applicableDays = formData.applicableDays;

	// Validate applicableDays
	if (applicableDays) {
		if (applicableDays.length == 0) {
			return {
				valid: false,
				message: 'Validation error: select day of week!'
			};
		}

		if (applicableDays.some(d => d > 6 || d < 0)) {
			return {
				valid: false,
				message: 'Validation error: invalid day of week!'
			};
		}
	}

	// Validate calendar intervals
	const {
		intervals: [calendarIntervalStart, calendarIntervalEnd]
	} = formData;
	const calendarIntervalMinutesDiff = Math.abs(calendarIntervalEnd.diff(calendarIntervalStart, 'minutes'));

	if (calendarIntervalMinutesDiff > 60 * 24) {
		return {
			valid: false,
			message: 'Validation error: intervals over 24 hours are not allowed.'
		};
	}

	// Minimal interval granularity is 1 hour, if less, it is most probably a zero-interval
	if (calendarIntervalMinutesDiff < 59) {
		return {
			valid: false,
			message: 'Validation error: zero-intervals are not allowed.'
		};
	}

	// Validate intervals
	const intervals = getIntervals(formData);
	const [intervalStart, intervalEnd] = intervals[0];

	if (intervalStart < 0 || intervalEnd > 24) {
		return {
			valid: false,
			message: 'Validation error: invalid hours!'
		};
	}

	if (!allowUtcMidnightCrossingRanges && intervalStart >= intervalEnd) {
		return {
			valid: false,
			message: 'Validation error: UTC multi day ranges are not allowed!'
		};
	}

	return { valid: true, message: '' };
};

export const validateSpeedFactorOnUpdate = (calendarEvent: CalendarEvent, formData: EventFormData) => {
	const formDataValidation = validateFormData(formData);
	if (!formDataValidation.valid) {
		return formDataValidation;
	}
	const [nextStart, nextEnd] = formData.intervals;
	const [prevStart, prevEnd] = [moment(calendarEvent.start).utc(), moment(calendarEvent.end).utc()];

	if (!prevStart.isSame(nextStart.clone().utc(), 'year') || !prevEnd.isSame(nextEnd.clone().utc(), 'year')) {
		return {
			valid: false,
			message: `Event can't cross UTC year boundaries or be dragged to another year.`
		};
	}

	return { valid: true, message: '' };
};
